/**
 * XENERGY brand mark — the X-badge + wordmark unit used across the PWA.
 *
 * Visual recipe:
 *   - Circular badge with a centred "X" letter
 *   - Wordmark "XENERGY" in bold next to the badge
 *   - Optional subtitle below the wordmark
 *
 * Accent prop:
 *   - 'primary' (default) — primary-blue badge, white X. Cohesive with the
 *     rest of the PWA's blue brand. This is the canonical PWA mark.
 *   - 'amber' — amber-500 badge, slate-900 X. Mirrors the desktop sidebar's
 *     historical mark exactly. Available for parity with the desktop or for
 *     dark surfaces where the amber pops.
 *
 * Variant prop (controls text colour for the wordmark + subtitle):
 *   - 'light' (default) — for use on the canvas (gray-50) background, e.g.
 *     Home and Login. Wordmark is gray-900, subtitle is gray-500.
 *   - 'dark' — for use on dark surfaces (slate-900 sheets, splash). Wordmark
 *     is white, subtitle is slate-400.
 *
 * Sizes:
 *   - 'sm' — 32×32 badge, used inline in compact headers.
 *   - 'md' (default) — 36×36 badge, matches desktop sidebar size.
 *   - 'lg' — 56×56 badge, used on Login splash where the brand stands alone.
 */

const SIZE_CLASSES = {
  sm: { badge: 'w-8 h-8 text-base',  wordmark: 'text-small',   subtitle: 'text-caption' },
  md: { badge: 'w-9 h-9 text-lg',    wordmark: 'text-small',   subtitle: 'text-caption' },
  lg: { badge: 'w-14 h-14 text-2xl', wordmark: 'text-display', subtitle: 'text-small'   },
};

const ACCENTS = {
  // Default — primary-blue circle, white X. Matches the rest of the PWA's
  // blue UI; avoids the amber-on-blue clash the desktop has.
  primary: 'bg-primary text-white',
  // Heritage — amber circle, slate-900 X. Matches frontend/Layout.jsx exactly.
  amber:   'bg-brand-amber text-sidebar',
};

export default function Brand({
  size     = 'md',
  variant  = 'light',
  accent   = 'amber',
  subtitle = 'ERP Mobile',
  className = '',
}) {
  const s              = SIZE_CLASSES[size]   ?? SIZE_CLASSES.md;
  const accentClasses  = ACCENTS[accent]      ?? ACCENTS.primary;
  const wordmarkColor  = variant === 'dark' ? 'text-white'      : 'text-gray-900';
  const subtitleColor  = variant === 'dark' ? 'text-slate-400'  : 'text-gray-500';

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div
        className={`${s.badge} ${accentClasses} rounded-full flex items-center justify-center font-bold shrink-0`}
        aria-hidden="true"
      >
        X
      </div>
      <div className="min-w-0">
        <div className={`${s.wordmark} font-bold leading-tight ${wordmarkColor}`}>
          XENERGY
        </div>
        {subtitle && (
          <div className={`${s.subtitle} ${subtitleColor} truncate`}>
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );
}
